import { removeArrayDuplications, getPointsTextColor } from "utils/HelperFunctions";
import moment from "moment";
import { displayDate } from "utils/Date";
import { generateColorFromString } from "utils/GenerateColorFromString/GenerateColorFromString";
import _loadash from "lodash";
import { THEMES, ACTIVE } from "lib/constants";
import { MaterialUIControllerProvider } from "context";
import { ThemeProvider } from "@mui/material/styles";
import ReactDOM from "react-dom";
import UserFilterDisplay from "pages/Performance/components/ExportData/UserFilterDisplay";
import html2canvas from "html2canvas";
import JsPDF from "jspdf";
import "jspdf-autotable";
import theme from "../../assets/theme";

export const generateTableData = (data) => {
  const tableData = [];

  data?.forEach((user) => {
    tableData.push({
      id: user.userData.id,
      name: user.name,
      averagePoints: user.avgTotalPoint,
      averagePointTrend: user.avgPointTrend,
      trendTotalPoints: user.trendTotalPoints,
      averageKPIs: user.avgTotalKPIs,
      totalCount: user.totalCount,
      totalPoint: user.totalPoint,
      totalAvg: user.totalAvg,
      location: user.location,
      team: user.team,
      role: user.role,
      reportsToName: user.reportsToName,
      inputKPIPermission: user.inputKPIPermission,
      status: user?.userData?.UserCompaniesLinks?.[0]?.FK_user_workspace_statuses_status || ACTIVE,
      disabled: !!user?.userData?.UserCompaniesLinks?.[0]?.disabledAt,
    });
  });

  // Only count members into averages when they have entered KPIs
  const membersWithCount = tableData.filter((user) => user.totalCount > 0);
  const totalMembersData = tableData.reduce(
    (sum, individualUser) => ({
      totalPoint: parseInt(sum?.totalPoint, 10) + parseInt(individualUser?.totalPoint, 10),
      totalCount: parseInt(sum?.totalCount, 10) + parseInt(individualUser?.totalCount, 10),
      // eslint-disable-next-line no-unsafe-optional-chaining
      averagePoints: +sum?.averagePoints + +individualUser?.averagePoints,
      averageKPIs: parseFloat(sum?.averageKPIs, 0) + parseFloat(individualUser?.averageKPIs, 0),
      totalAvg: parseFloat(sum?.totalAvg, 0) + parseFloat(individualUser?.totalAvg, 0),
      averagePointTrend:
        parseFloat(sum?.averagePointTrend, 0) + parseFloat(individualUser?.averagePointTrend, 0),
      trendTotalPoints:
        parseFloat(sum?.trendTotalPoints, 0) + parseFloat(individualUser?.trendTotalPoints, 0),
    }),
    {
      totalPoint: 0,
      totalCount: 0,
      averagePoints: 0,
      averageKPIs: 0,
      totalAvg: 0,
      averagePointTrend: 0,
      trendTotalPoints: 0,
    }
  );

  // All members inputKPIPermission was set to false only if the inputKPIPermission for all users was false
  totalMembersData.inputKPIPermission = !tableData.every((user) => !user.inputKPIPermission);

  totalMembersData.averagePoints = membersWithCount.length
    ? (totalMembersData.averagePoints / membersWithCount.length).toFixed(1)
    : 0;
  totalMembersData.averageKPIs = membersWithCount.length
    ? (totalMembersData.averageKPIs / membersWithCount.length).toFixed(1)
    : 0;
  totalMembersData.name = "All Members";
  totalMembersData.id = "all";
  totalMembersData.status = ACTIVE;

  totalMembersData.totalAvg = membersWithCount.length
    ? (totalMembersData.totalAvg / membersWithCount.length).toFixed(1)
    : 0;

  totalMembersData.averagePointTrend = membersWithCount.length
    ? (totalMembersData.averagePointTrend / membersWithCount.length).toFixed(1)
    : 0;
  totalMembersData.trendTotalPoints = membersWithCount.length
    ? (totalMembersData.trendTotalPoints / membersWithCount.length).toFixed(1)
    : 0;

  return [totalMembersData, ...tableData];
};

const reduceDateArray = (dateArray) =>
  dateArray.reduce(
    (sum, individualUser) => ({
      points: parseInt(sum.points, 10) + parseInt(individualUser.points, 10),
      count: parseInt(sum.count, 10) + parseInt(individualUser.count, 10),
      dailyTarget: parseInt(sum.dailyTarget, 10) + parseInt(individualUser.dailyTarget, 10),
      outOfOffice: sum.outOfOffice ? !!individualUser.outOfOffice : false,
    }),
    {
      points: 0,
      count: 0,
      dailyTarget: 0,
      outOfOffice: true,
    }
  );

function calculateLinearTrendline(dataset, n) {
  // const n = dataset.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;

  for (let i = 0; i < n; i += 1) {
    const y = dataset[i];
    sumX += i;
    sumY += y;
    sumXY += i * y;
    sumX2 += i * i;
  }

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  const trendline = dataset.map((y, x) => ({
    x,
    y: slope * x + intercept,
  }));

  return trendline;
}

const generateGraphData = (
  BEData,
  activeUserId,
  activity,
  dateFilter,
  dateFormat,
  timezone,
  daysOfWeek,
  graphDisplay
) => {
  // State Initialization for graph data
  const chartData = {
    labels: [],
    datasets: [],
  };

  let dates = [];
  const dow = daysOfWeek.split("");
  const dowNum = dow.map(Number);

  if (activity === "Total Performance") {
    if (
      dateFilter?.toLowerCase()?.indexOf("quarter") < 0 &&
      dateFilter?.toLowerCase()?.indexOf("year") < 0
    ) {
      // Total Performance - Days/Months
      BEData?.forEach((user) => {
        if (activeUserId === "all" || user?.userData.id === activeUserId) {
          user?.totalPerformance?.forEach((performance) => {
            if (dowNum[moment(performance?.date).day()])
              dates?.push(moment(performance?.date)?.format("YYYY-MM-DD"));
          });
        }
      });
      dates = removeArrayDuplications(dates)?.sort((a, b) => (a > b ? 1 : -1));

      const allKpisTracked = {};
      const dailyKpisTracked = [];
      const totalPerformance = dates?.map((date) => {
        const dateArray = BEData?.reduce((dateEntries, user) => {
          if (activeUserId === "all" || activeUserId === user.userData.id) {
            const data = user.totalPerformance
              .filter((performance) => performance.date === date)
              .reduce(
                (kpiDetails, kpi) => ({
                  points: parseInt(kpiDetails.points || 0, 10) + parseInt(kpi.points, 10),
                  count: parseInt(kpiDetails.count || 0, 10) + parseInt(kpi.count, 10),
                }),
                { points: 0, count: 0 }
              );

            dateEntries.push({
              date,
              points: data.points,
              count: data.count,
              // dailyTarget: user.dailyTarget,
              dailyTarget: user?.outOfOffice?.includes(date) ? 0 : user.dailyTarget,
              outOfOffice: user?.outOfOffice?.includes(date),
            });
          }
          return dateEntries;
        }, []);

        if (graphDisplay === "kpiBreakdown") {
          const datasets = { outOfOffice: true };

          // Stacked bar
          const allDailyKpisTracked = BEData?.filter(
            (user) => activeUserId === "all" || activeUserId === user.userData.id
          )
            .flatMap((user) => {
              if (datasets.outOfOffice) datasets.outOfOffice = user?.outOfOffice?.includes(date);
              return user.kpiActiontotal || [];
            })
            .filter(
              (action) => moment.tz(action.createdAt, timezone).format("YYYY-MM-DD") === date
            );

          if (allDailyKpisTracked.length) {
            const combinedTotals = allDailyKpisTracked
              .reduce((totals, action) => {
                const index = totals.findIndex((kpi) => kpi.kpiId === action.kpiActionId);
                if (index !== -1) {
                  totals[index].total += action.totalCounts;
                } else {
                  totals.push({
                    kpiId: action.kpiActionId,
                    total: action.totalCounts,
                    kpiName: action.kpiActionName,
                  });
                }
                return totals;
              }, [])
              .sort((a, b) => b.total - a.total);

            let acc = 0;
            combinedTotals.forEach((kpi) => {
              allKpisTracked[kpi.kpiId] = kpi.kpiName;
              datasets[kpi.kpiId] = {
                position: [acc, kpi.total + acc],
                dailyCount: kpi.total,
              };
              acc += kpi.total;
            });
          }
          dailyKpisTracked.push(datasets);
        }

        const reducedDateArray = reduceDateArray(dateArray);
        return { date, ...reducedDateArray };
      });

      chartData.labels = dates.map((date) => displayDate(date, "dddd MM-DD"));
      if (graphDisplay === "achievedPointsBreakdown") {
        chartData.datasets = [
          {
            label: "Achieved Points",
            data: totalPerformance.map((item) => item.points),
            outOfOffice: totalPerformance.map((item) => item.outOfOffice),
            color: totalPerformance.map((item) =>
              getPointsTextColor(item.points, item.dailyTarget)
            ),
            chartType: "bar",
          },
          {
            label: "Daily Goal",
            color: "#dedede",
            data: totalPerformance.map((item) => item.dailyTarget ?? 10),
            chartType: "bar",
          },
        ];
      } else {
        chartData.datasets = Object.entries(allKpisTracked).map(([kpiId, kpiName]) => ({
          label: kpiName,
          data: dailyKpisTracked.map((dailyKpis) =>
            kpiId in dailyKpis ? dailyKpis[kpiId].position : 0
          ),
          outOfOffice: dailyKpisTracked.map((item) => item.outOfOffice),
          color: generateColorFromString(kpiName),
          chartType: "stacked-bar",
          dailyCount: dailyKpisTracked.map((dailyKpis) =>
            kpiId in dailyKpis ? dailyKpis[kpiId].dailyCount : 0
          ),
        }));
        chartData.datasets.unshift({
          label: "Tracked KPIs",
          data: totalPerformance.map((item) => item.count),
          color: generateColorFromString(activity),
          chartType: "bar",
        });
      }
    } else {
      // Total Performance - Quarter/Year
      let months = [];
      BEData?.forEach((user) => {
        if (activeUserId === "all" || user?.userData.id === activeUserId) {
          user?.totalPerformance?.forEach((performance) => {
            months?.push(performance?.date);
          });
        }
      });
      months = removeArrayDuplications(months);

      const allKpisTracked = {};
      const monthlyKpisTracked = [];
      const totalPerformance = months.map((month) => {
        const dateArray = BEData?.reduce((dateEntries, user) => {
          if (activeUserId === "all" || activeUserId === user.userData.id) {
            const data = user.totalPerformance
              .filter((performance) => performance.date === month)
              .reduce(
                (kpiDetails, kpi) => ({
                  points: parseInt(kpiDetails.points || 0, 10) + parseInt(kpi.points, 10),
                  count: parseInt(kpiDetails.count || 0, 10) + parseInt(kpi.count, 10),
                  dailyTarget: parseInt(kpiDetails.dailyTarget, 10) + parseInt(kpi.dailyTarget, 10),
                }),
                { points: 0, count: 0, dailyTarget: 0 }
              );

            dateEntries.push({
              date: month,
              points: data.points,
              count: data.count,
              dailyTarget: data.dailyTarget,
            });
          }
          return dateEntries;
        }, []);

        if (graphDisplay === "kpiBreakdown") {
          // Stacked bar
          const allMonthlyKpisTracked = BEData?.filter(
            (user) => activeUserId === "all" || activeUserId === user.userData.id
          )
            .flatMap((user) => user.kpiActiontotal || [])
            .filter((action) => action.createdAt === month);

          const datasets = {};
          if (allMonthlyKpisTracked.length) {
            const combinedTotals = allMonthlyKpisTracked
              .reduce((totals, action) => {
                const index = totals.findIndex((kpi) => kpi.kpiId === action.kpiActionId);
                if (index !== -1) {
                  totals[index].total += action.totalCounts;
                } else {
                  totals.push({
                    kpiId: action.kpiActionId,
                    total: action.totalCounts,
                    kpiName: action.kpiActionName,
                  });
                }
                return totals;
              }, [])
              .sort((a, b) => b.total - a.total);

            let acc = 0;
            combinedTotals.forEach((kpi) => {
              allKpisTracked[kpi.kpiId] = kpi.kpiName;
              datasets[kpi.kpiId] = {
                position: [acc, kpi.total + acc],
                dailyCount: kpi.total,
              };
              acc += kpi.total;
            });
          }
          monthlyKpisTracked.push(datasets);
        }

        const reducedDateArray = reduceDateArray(dateArray);
        return { date: month, ...reducedDateArray };
      });

      const labels = [];
      months.forEach((m) => {
        labels.push(moment(m).format("MMMM"));
      });
      chartData.labels = labels;

      if (graphDisplay === "achievedPointsBreakdown") {
        chartData.datasets = [
          {
            label: "Achieved Points",
            data: totalPerformance.map((item) => item.points),
            color: totalPerformance.map((item) =>
              getPointsTextColor(item.points, item.dailyTarget)
            ),
            chartType: "bar",
          },
          {
            label: "Daily Goal",
            color: "#dedede",
            data: totalPerformance.map((item) => item.dailyTarget ?? 10),
            chartType: "bar",
          },
        ];
      } else {
        chartData.datasets = Object.entries(allKpisTracked).map(([kpiId, kpiName]) => ({
          label: kpiName,
          data: monthlyKpisTracked.map((dailyKpis) =>
            kpiId in dailyKpis ? dailyKpis[kpiId].position : 0
          ),
          color: generateColorFromString(kpiName),
          chartType: "stacked-bar",
          dailyCount: monthlyKpisTracked.map((dailyKpis) =>
            kpiId in dailyKpis ? dailyKpis[kpiId].dailyCount : 0
          ),
        }));
        chartData.datasets.unshift({
          label: "Tracked KPIs",
          data: totalPerformance.map((item) => item.count),
          color: generateColorFromString(activity),
          chartType: "bar",
        });
      }
    }
  } else {
    // eslint-disable-next-line no-lonely-if
    if (
      dateFilter?.toLowerCase()?.indexOf("quarter") < 0 &&
      dateFilter?.toLowerCase()?.indexOf("year") < 0
    ) {
      // Individual KPIs - Days/Months
      BEData.forEach((user) => {
        if (activeUserId === "all" || user.userData.id === activeUserId) {
          user?.totalPerformance?.forEach((performance) => {
            if (dowNum[moment(performance?.date).day()])
              dates.push(moment(performance.date).format("YYYY-MM-DD"));
          });
        }
      });
      dates = removeArrayDuplications(dates).sort((a, b) => (a > b ? 1 : -1));
      const KpiWisePerformance = dates.map((date) => {
        const dateArray = [];
        BEData.forEach((user) => {
          if (activeUserId === "all" || activeUserId === user.userData.id) {
            const data =
              user.kpiActiontotal &&
              user.kpiActiontotal.find(
                (action) =>
                  action.kpiActionName === activity &&
                  moment.tz(action.createdAt, timezone).format("YYYY-MM-DD") === date
              );
            dateArray.push({
              date,
              points: data ? data.totalPoints : 0,
              count: data ? data.totalCounts : 0,
            });
          }
        });

        const reducedDateArray = reduceDateArray(dateArray);
        return { date, ...reducedDateArray };
      });
      chartData.labels = dates.map((date) => displayDate(date, "dddd MM-DD"));

      if (graphDisplay === "achievedPointsBreakdown") {
        chartData.datasets = [
          {
            label: "Achieved Points",
            color: generateColorFromString(activity),
            data: KpiWisePerformance.map((item) => item.points),
            chartType: "bar",
          },
        ];
      } else {
        chartData.datasets = [
          {
            label: "Tracked KPIs",
            color: generateColorFromString(activity),
            data: KpiWisePerformance.map((item) => item.count),
            chartType: "bar",
          },
        ];
      }
    } else {
      // Individual KPIs - Quarter/Year
      let months = [];
      BEData.forEach((user) => {
        if (activeUserId === "all" || user.userData.id === activeUserId) {
          user?.totalPerformance?.forEach((performance) => {
            months.push(performance.date);
          });
        }
      });
      months = removeArrayDuplications(months);
      const KpiWisePerformance = months.map((month) => {
        const dateArray = [];
        BEData.forEach((user) => {
          if (activeUserId === "all" || activeUserId === user.userData.id) {
            const data =
              user.kpiActiontotal &&
              user.kpiActiontotal.find(
                (action) =>
                  action.kpiActionName === activity &&
                  moment.tz(action.createdAt, timezone).format("YYYY-MM") === month
              );
            dateArray.push({
              month,
              points: data ? data.totalPoints : 0,
              count: data ? data.totalCounts : 0,
            });
          }
        });
        const reducedDateArray = reduceDateArray(dateArray);
        return { date: month, ...reducedDateArray };
      });
      const labels = [];
      months.forEach((m) => {
        labels.push(moment(m).format("MMMM"));
      });
      chartData.labels = labels;
      if (graphDisplay === "achievedPointsBreakdown") {
        chartData.datasets = [
          {
            label: "Achieved Points",
            color: generateColorFromString(activity),
            data: KpiWisePerformance.map((item) => item.points),
            chartType: "bar",
          },
        ];
      } else {
        chartData.datasets = [
          {
            label: "Tracked KPIs",
            color: generateColorFromString(activity),
            data: KpiWisePerformance.map((item) => item.count),
            chartType: "bar",
          },
        ];
      }
    }
  }

  // Mixed graph
  if (chartData.datasets.length) {
    // Normalize individual kpis
    if (activity !== "Total Performance") {
      const max = Math.max(...chartData.datasets[0].data);
      chartData.datasets[0].ymax = max;
    }

    const total = chartData.datasets[0].data?.reduce((a, x) => a + x, 0);
    chartData.datasets[0].total = total;

    // OOO dataset
    // if (chartData.datasets[0].outOfOffice?.length) {
    //   const oooData = [];
    //   for (let i = 0; i < chartData.datasets[0].outOfOffice.length; i += 1) {
    //     oooData.push(chartData.datasets[0].outOfOffice[i] ? chartData.datasets[1].data[i] : 0);
    //   }
    //   chartData.datasets.push({
    //     chartType: "outOfOffice",
    //     data: oooData,
    //     label: "Out of Office",
    //     color: "#eee",
    //   });
    // }

    // Trend line
    const trendline = [...chartData.datasets[0].data];
    while (trendline[trendline.length - 1] === 0) trendline.pop(); // remove trailing 0's
    // while (trendline.indexOf(0) === 0) trendline.shift(); // remove leading 0's - data doesn't scale well

    const nonZero = trendline.filter((x) => x !== 0);
    const linearTrendline =
      nonZero.length > 1
        ? calculateLinearTrendline(chartData.datasets[0].data, trendline.length)
        : 0;
    if (linearTrendline) {
      chartData.datasets.push({
        chartType: "trend-line",
        data: linearTrendline,
        label: "Trendline",
        color: "#3c7ba3",
      });
    }
  }

  return chartData;
};

function generateCircleDataset(center, graphBounds, radius, step) {
  // Equation of circle (x−h)^2 + (y−k)^2 = r^2
  // (x, y) - point on circle
  // (h, k) - center of circle
  const dataset = [{ x: 0, y: graphBounds.y * 1.1 }]; // This is so the colors of the rings fill the whole section

  // Left part of circle -> graph boundary Y
  if (graphBounds.y / 10 <= center.y) {
    // Plot a point on the graph boundary to eliminate gaps
    const delta = radius ** 2 - (graphBounds.y / 10 - center.y) ** 2;
    if (delta >= 0) {
      const x = center.x - Math.sqrt(delta);
      dataset.push({ x, y: graphBounds.y });
    }
  } else {
    // Extend dataset vertically until graph bound before quarter circle
    const x = center.x - radius;
    for (let { y } = graphBounds; y >= center.y; y -= (graphBounds.y - center.y) / (step / 10)) {
      dataset.push({ x, y: y * 10 });
    }
  }

  // Plot the circle
  for (let i = 0; i < step; i += 1) {
    const a = (1 + (0.5 / step) * i) * Math.PI;
    const x = center.x + Math.cos(a) * radius;
    const y = center.y + Math.sin(a) * radius;
    if (x <= graphBounds.x && y * 10 <= graphBounds.y) {
      dataset.push({ x, y: y * 10 });
    }
  }

  // Bottom of circle -> graph boundary X
  if (graphBounds.x <= center.x) {
    // Plot a point on the graph boundary to eliminate gaps
    const delta = radius ** 2 - (graphBounds.x - center.x) ** 2;
    if (delta >= 0) {
      const y = 10 * (center.y - Math.sqrt(delta));
      dataset.push({ x: graphBounds.x, y });
    }
  } else {
    // Extend dataset horizontally until graph bound after quarter circle
    const y = 10 * (center.y - radius);
    for (let { x } = center.x; x <= graphBounds.x; x += (graphBounds.x - center.x) / (step / 10)) {
      dataset.push({ x, y });
    }

    // Last data point on the graph boundary
    dataset.push({ x: graphBounds.x, y });
  }

  return dataset;
}

function isPointInsideCircle(c, radius, p) {
  // Since the zones are only quarter circles, handle edge cases where point > center
  if (p.x >= c.x && p.y / 10 >= c.y) return true;
  if (p.x > c.x) {
    return p.y / 10 > c.y - radius;
  }
  if (p.y / 10 > c.y) {
    return p.x > c.x - radius;
  }
  const distance = Math.sqrt((p.x - c.x) ** 2 + (p.y / 10 - c.y) ** 2);
  return distance <= radius;
}

export const generateScatterChartData = (data, activeUserId) => {
  const labels = [];
  const dataset = [];

  data.forEach((user) => {
    labels.push(user.name);
    dataset.push({
      x: +user.scatterAvgPoints,
      y: +user.scatterAttainment,
      label: user.name,
      userId: user.id,
      team: user.team,
      reportsTo: user.reportsToName,
    });
  });

  let yMax = dataset.length ? Math.max(...dataset.map((d) => d.y)) : 100;
  let xMax = dataset.length ? Math.max(...dataset.map((d) => d.x)) : 10;
  if (yMax < 100) yMax = 100;
  if (xMax < 10) xMax = 10;

  let graphXMax = Math.ceil(xMax);
  if (graphXMax % 2) {
    graphXMax += 1;
  }
  const graphYMax = Math.ceil(yMax / 10) * 10;
  const graphBounds = { x: graphXMax, y: graphYMax };
  const midpoint = { x: 15, y: 15 };

  const chartData = {
    datasets: [
      {
        labels,
        data: dataset,
        selected: activeUserId,
        pointBackgroundColor: (context) => {
          const index = context.dataIndex;
          const value = context.dataset.data[index];
          const circleCenter = { x: 15, y: 15 };

          // Gray out non-selected users
          if (context.dataset.selected !== "all" && context.dataset.selected !== value.userId) {
            return "#ccc";
          }
          if (isPointInsideCircle(circleCenter, 9, value)) return "#00d0ff";
          if (isPointInsideCircle(circleCenter, 12, value)) return THEMES.PRIMARY.GREEN;
          if (isPointInsideCircle(circleCenter, 15, value)) return "#FF7E01";
          return THEMES.PRIMARY.RED;
        },
        pointBorderWidth: 0,
        pointBorderColor: "#969798",
        pointHoverRadius: 15,
        // pointHoverBackgroundColor: "rgba(233, 120, 100, 1)",
        // pointHoverBorderColor: "rgba(233, 120, 100, 1)",
        pointHoverBorderWidth: 0,
        pointRadius: 13,
        pointHitRadius: 1,
      },
      {
        label: "Zone 1",
        data: generateCircleDataset(midpoint, graphBounds, 15, 50),
        borderColor: "#969798",
        borderWidth: 2,
        pointRadius: 0,
        showLine: true,
        tension: 0,
        datalabels: {
          labels: {
            title: null,
          },
        },
        fill: "start",
        backgroundColor: "#F83A4610",
      },
      {
        label: "Zone 2",
        data: generateCircleDataset(midpoint, graphBounds, 12, 50),
        borderColor: "#969798",
        borderWidth: 2,
        pointRadius: 0,
        showLine: true,
        tension: 0,
        datalabels: {
          labels: {
            title: null,
          },
        },
        fill: "-1",
        backgroundColor: "#FF7E0110",
      },
      {
        label: "Zone 3",
        data: generateCircleDataset(midpoint, graphBounds, 9, 50),
        borderColor: "#969798",
        borderWidth: 2,
        pointRadius: 0,
        showLine: true,
        tension: 0,
        datalabels: {
          labels: {
            title: null,
          },
        },
        fill: "-1",
        backgroundColor: "#02c29510",
      },
      {
        label: "Zone 4",
        data: [
          { x: 0, y: graphBounds.y },
          { x: graphBounds.x, y: graphBounds.y },
          { x: graphBounds.x, y: 0 },
        ],
        showLine: false,
        datalabels: {
          labels: {
            title: null,
          },
        },
        fill: "-1",
        backgroundColor: "#00d0ff10",
      },
    ],
  };
  return { chartData, graphBounds };
};

export const generateDonutChartData = (BEData, activeUserId) => {
  const pieChart = {
    kpiBreakdown: {
      labels: [],
      data: [],
      graphKpiColors: [],
    },

    achivedPointsBreakdown: {
      labels: [],
      data: [],
      graphKpiColors: [],
    },
  };

  const allKpisData = [];
  let filterdData = [];
  if (activeUserId === "all") {
    filterdData = BEData;
  } else {
    filterdData = BEData.filter((user) => user.userData.id === activeUserId);
  }
  filterdData?.forEach((entry) => {
    if (entry?.kpiActiontotal?.length) {
      entry?.kpiActiontotal.forEach((kpiAction) => allKpisData.push(kpiAction));
    }
  });
  const groupedData = _loadash.groupBy(allKpisData, "kpiActionName");
  Object.keys(groupedData).forEach((key) => {
    pieChart.kpiBreakdown.labels.push(key);
    pieChart.achivedPointsBreakdown.labels.push(key);
    pieChart.kpiBreakdown.graphKpiColors.push(generateColorFromString(key));
    pieChart.achivedPointsBreakdown.graphKpiColors.push(generateColorFromString(key));
    const sumData = groupedData[key].reduce(
      (sum, individualUser) => ({
        totalPoints: parseInt(sum?.totalPoints, 10) + parseInt(individualUser?.totalPoints, 10),
        totalCounts: parseInt(sum?.totalCounts, 10) + parseInt(individualUser?.totalCounts, 10),
      }),
      {
        totalPoints: 0,
        totalCounts: 0,
      }
    );
    pieChart.kpiBreakdown.data.push(sumData.totalCounts);
    pieChart.achivedPointsBreakdown.data.push(sumData.totalPoints);
  });
  return pieChart;
};

export const convertBEData = (
  BEData,
  activeUserId,
  activePerformanceIndex,
  dateFilter,
  dateFormat,
  timezone,
  daysOfWeek,
  graphDisplay,
  refreshOpts
) => {
  let listingData = [];
  let structuredGraphData = {
    labels: [],
    datasets: [],
  };
  let structuredTableData = [];
  BEData?.forEach((user) => {
    if (activeUserId === "all") {
      user?.kpiActiontotal?.forEach((activity) => {
        listingData.push(activity?.kpiActionName?.trim());
      });
    }
    if (user.userData.id === activeUserId) {
      user?.kpiActiontotal?.forEach((activity) => {
        listingData.push(activity?.kpiActionName?.trim());
      });
    }
  });

  listingData = [
    "Total Performance",
    ...removeArrayDuplications(listingData).sort((a, b) => (a > b ? 1 : -1)),
  ];

  structuredTableData = generateTableData(BEData);

  structuredGraphData = generateGraphData(
    BEData,
    activeUserId,
    listingData[activePerformanceIndex],
    dateFilter,
    dateFormat,
    timezone,
    daysOfWeek,
    graphDisplay
  );

  let structuredPieChartData = {};
  if (refreshOpts.refreshPie) structuredPieChartData = generateDonutChartData(BEData, activeUserId);

  return {
    listingData,
    structuredGraphData,
    structuredTableData,
    structuredPieChartData,
  };
};

function chunkArray(array, chunkSize) {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

function prepareTablesData(performance) {
  // Get all unique KPIs
  const uniqueKpis = [
    ...new Set(
      performance?.reportingUsers?.flatMap((user) =>
        user.kpiActiontotal.map((kpi) => kpi.kpiActionName)
      )
    ),
  ];

  // Filter out users who don't have any data
  const usersWithCompletedKpi = performance?.reportingUsers?.filter(
    (user) => user.kpiActiontotal.length > 0
  );

  // Get all counts and points for the entire org
  const organizationCountsByKpiName = {};
  const organizationPointsByKpiName = {};
  usersWithCompletedKpi?.forEach((user) => {
    user.kpiActiontotal.forEach((kpi) => {
      if (organizationCountsByKpiName[kpi.kpiActionName]) {
        organizationCountsByKpiName[kpi.kpiActionName] += kpi.totalCounts;
      } else {
        organizationCountsByKpiName[kpi.kpiActionName] = kpi.totalCounts;
      }

      if (organizationPointsByKpiName[kpi.kpiActionName]) {
        organizationPointsByKpiName[kpi.kpiActionName] += kpi.totalPoints;
      } else {
        organizationPointsByKpiName[kpi.kpiActionName] = kpi.totalPoints;
      }
    });
  });

  // Split users into chunks of 5 which means 5 users per table
  // This is done to prevent the table from being too wide
  const userChunks = chunkArray(usersWithCompletedKpi, 5);
  const tablesData = userChunks.map((userChunk) => {
    const headersOfCompletedKpis = ["COMPLETED KPIS", "Organization"].concat(
      userChunk.map((user) => user.name)
    );

    const headersOfAchievedPoints = ["ACHIEVED POINTS", "Organization"].concat(
      userChunk.map((user) => user.name)
    );

    const data = uniqueKpis.map((kpiName) => {
      // Set up data for kpiName and its org total counts and points
      const rowCounts = [kpiName, organizationCountsByKpiName[kpiName]];
      const rowPoints = [kpiName, organizationPointsByKpiName[kpiName]];
      userChunk.forEach((user) => {
        // Combine all the points & counts for the same KPI name by user
        const kpiActiontotalUnique = user.kpiActiontotal.reduce((acc, curr) => {
          const existingIndex = acc.findIndex((item) => item.kpiActionName === curr.kpiActionName);
          if (existingIndex > -1) {
            acc[existingIndex].totalPointsByKpiName += curr.totalPoints;
            acc[existingIndex].totalCountsByKpiName += curr.totalCounts;
          } else {
            acc.push({
              ...curr,
              totalPointsByKpiName: curr.totalPoints,
              totalCountsByKpiName: curr.totalCounts,
            });
          }
          return acc;
        }, []);

        const userKpi = kpiActiontotalUnique.find((action) => action.kpiActionName === kpiName);
        rowCounts.push(userKpi ? userKpi.totalCountsByKpiName : 0);
        rowPoints.push(userKpi ? userKpi.totalPointsByKpiName : 0);
      });
      return { rowCounts, rowPoints };
    });

    return {
      completedKpis: {
        headers: headersOfCompletedKpis,
        body: data.map((item) => item.rowCounts),
      },
      achievedPoints: {
        headers: headersOfAchievedPoints,
        body: data.map((item) => item.rowPoints),
      },
    };
  });

  return tablesData;
}

export const generatePDF = async (performance, filters) => {
  const pdf = new JsPDF();

  /*
   *  Setting up this is necessary because we don't have UserFilterDisplay on the web page
   *  so we need to render it into a div and take a screenshot of it
   *  and remove it after we're done
   */
  // Create a new div and append it to the body
  const div = document.createElement("div");
  document.body.appendChild(div);
  // Render the UserFilterDisplay into the div
  ReactDOM.render(
    <MaterialUIControllerProvider>
      <ThemeProvider theme={theme}>
        <UserFilterDisplay filters={filters} />
      </ThemeProvider>
    </MaterialUIControllerProvider>,
    div
  );

  // Set up the code blocks' ids that we want to take screenshot from
  const codeBlockIds = ["userFilterDisplay", "performanceBarGraph", "performanceAnalytics"];
  const padding = 10;
  const verticalPadding = 5;
  const pdfPageWidth = pdf.internal.pageSize.getWidth();
  let startHeight = 10;

  // Take screenshots of the code blocks and add them to the pdf
  const promises = codeBlockIds.map(async (id) => {
    const input = document.getElementById(id);
    const returnArray = [id];
    if (input) {
      // This is needed because html2canvas doesn't work well with box shadows, it generates weird gray color on screnshots
      if (id === "performanceBarGraph") {
        const performanceBarGraphCardPart1 = document.getElementById(
          "performanceBarGraphCardPart1"
        );
        const performanceBarGraphCardPart2 = document.getElementById(
          "performanceBarGraphCardPart2"
        );
        const originalBoxShadowP1 = performanceBarGraphCardPart1.style.boxShadow;
        const originalBoxShadowP2 = performanceBarGraphCardPart1.style.boxShadow;
        performanceBarGraphCardPart1.style.boxShadow = "none";
        performanceBarGraphCardPart2.style.boxShadow = "none";
        const canvas = await html2canvas(input);
        performanceBarGraphCardPart1.style.boxShadow = originalBoxShadowP1;
        performanceBarGraphCardPart2.style.boxShadow = originalBoxShadowP2;
        returnArray.push(canvas);
      } else if (id === "performanceAnalytics") {
        const performanceAnalyticsCard = document.getElementById("performanceAnalyticsCard");
        const originalBoxShadow = performanceAnalyticsCard.style.boxShadow;
        performanceAnalyticsCard.style.boxShadow = "none";
        const canvas = await html2canvas(input);
        performanceAnalyticsCard.style.boxShadow = originalBoxShadow;
        returnArray.push(canvas);
      } else {
        const canvas = await html2canvas(input);
        returnArray.push(canvas);
      }
    }
    return returnArray;
  });

  // Wait for all Promises to resolve
  const resolvedCanvasPromises = await Promise.all(promises);
  resolvedCanvasPromises.forEach(([id, canvas]) => {
    if (!canvas) return;
    const widthFactor = id === "performanceAnalytics" ? 0.8 : 1;
    const imgData = canvas.toDataURL("image/png");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = (pdfPageWidth - 2 * padding) * widthFactor;
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    const imgX = id === "performanceAnalytics" ? (pdfPageWidth - pdfWidth) / 2 : 10;

    // Add the element to the page
    pdf.addImage(imgData, "PNG", imgX, startHeight, pdfWidth, pdfHeight);

    // Make sure images don't overlap
    startHeight += pdfHeight;
    startHeight += verticalPadding;
  });

  // Remove the div from the body
  document.body.removeChild(div);

  // Done with screenshots, add new page for tables if there is any
  const tablesData = prepareTablesData(performance);
  if (tablesData.length > 0) {
    pdf.addPage();
  }
  const spaceBetweenTables = 20;

  // Create "Completed KPIs" tables
  tablesData.forEach((tableData, index) => {
    pdf.autoTable({
      startY: index === 0 ? 10 : pdf.autoTable.previous.finalY + spaceBetweenTables,
      head: [tableData.completedKpis.headers],
      body: tableData.completedKpis.body,
      pageBreak: "auto", // will break to next page if content exceeds the page
      headStyles: { fillColor: [248, 58, 70], valign: "middle" }, // Alysio red color
    });
  });

  // Create "Achieved Points" tables
  tablesData.forEach((tableData) => {
    pdf.autoTable({
      startY: pdf.autoTable.previous.finalY + spaceBetweenTables,
      head: [tableData.achievedPoints.headers],
      body: tableData.achievedPoints.body,
      pageBreak: "auto",
      headStyles: { fillColor: [248, 58, 70], valign: "middle" },
    });
  });

  return pdf;
};

export const convertBEDataForCsv = (BEData) => {
  const kpiObj = {};
  BEData?.forEach((user) => {
    user?.kpiActiontotal?.forEach((activity) => {
      kpiObj[activity.kpiActionId] = activity.kpiActionName;
    });
  });

  const kpiArray = Object.entries(kpiObj);
  kpiArray.sort((a, b) => a[1].localeCompare(b[1]));

  const accumulatedTotals = {};
  const tableData = [];
  BEData?.forEach((user) => {
    const userKpiData = user.kpiActiontotal.reduce((arr, entry) => {
      if (entry.kpiActionId in arr) {
        arr[entry.kpiActionId].count += entry.totalCounts;
        arr[entry.kpiActionId].points += entry.totalPoints;
      } else {
        arr[entry.kpiActionId] = { count: entry.totalCounts, points: entry.totalPoints };
      }

      // Accumulated totals
      if (entry.kpiActionId in accumulatedTotals) {
        accumulatedTotals[entry.kpiActionId].count += entry.totalCounts;
        accumulatedTotals[entry.kpiActionId].points += entry.totalPoints;
      } else {
        accumulatedTotals[entry.kpiActionId] = {
          count: entry.totalCounts,
          points: entry.totalPoints,
        };
      }
      return arr;
    }, {});

    tableData.push({ id: user.userData.id, name: user.name, kpiData: userKpiData });
  });

  const membersWithCount = tableData.filter((user) => Object.keys(user.kpiData).length > 0);
  const averageTotals = {};
  Object.keys(accumulatedTotals).forEach((key) => {
    averageTotals[key] = {
      count: (membersWithCount.length
        ? accumulatedTotals[key].count / membersWithCount.length
        : 0
      ).toFixed(1),
      points: (membersWithCount.length
        ? accumulatedTotals[key].points / membersWithCount.length
        : 0
      ).toFixed(1),
    };
  });

  tableData.unshift({ id: "allAverage", name: "Average", kpiData: averageTotals });
  tableData.unshift({ id: "all", name: "Total", kpiData: accumulatedTotals });

  return { kpiList: kpiArray, dataToExport: tableData };
};
